import type { Edge, Node } from '@xyflow/react'
import { v4 } from 'uuid'
import { SCREENING_NODE_TYPES, SCREENING_VOICEMAIL_PORT } from '../config/contact-screening.config'
import { VOICEMAIL_DETECTED_INTENT } from '../constants'
import type { AiAgentV2NodeData, FlowNodeTypes, NodeDataBase, RFInstance } from '../types'
import { getDefaultNodeData } from './index'

type VoicemailIntents = AiAgentV2NodeData['config']['intents']

export const getVoicemailIntent = (intents: VoicemailIntents) =>
  intents?.find((intent) => intent.intent_name === VOICEMAIL_DETECTED_INTENT.name)

export const getConnectedVoicemailNode = (rf: RFInstance, nodeId: string, intentId: string) => {
  const edge = rf
    .getNodeConnections({ nodeId, type: 'source' })
    .find((connection) => connection.sourceHandle === intentId)
  return edge ? rf.getNode(edge.target) : null
}

type BuilderNode = Node<NodeDataBase<unknown>>

/**
 * Save-direction dedupe for the screening voicemail port (SER-6070). The
 * panel's eager placeholder wiring (useVoicemailPortWiring) can race a
 * vibe-agent build: the user opens the screening panel after the node lands but
 * before the vibe agent's Voicemail Detected edge arrives, so the port ends up
 * with the auto-created placeholder AND the real edge. The port is
 * single-target by design, so any port with more than one edge keeps exactly
 * one: real (deletable) edges outrank the panel's non-deletable placeholders,
 * ties keep the first (edge order is stable, so the unsaved-changes
 * double-run stays symmetric). `deletable` does not survive the wire
 * round-trip — a reloaded pre-fix duplicate carries two undefined-deletable
 * edges, which is why the trigger is edge COUNT, not the placeholder flag.
 * A dropped placeholder's auto-minted (non-deletable) target goes with it
 * once nothing else points at it; reloaded targets are indistinguishable
 * from user nodes and stay. Pure: the canvas keeps the extra wire until the
 * next load; every save self-heals, including flows already saved with the
 * duplicate.
 */
export const dedupeScreeningVoicemailEdges = (
  nodes: BuilderNode[],
  edges: Edge[],
): { nodes: BuilderNode[]; edges: Edge[] } => {
  const dropEdges = new Set<string>()
  for (const node of nodes) {
    if (!node.type || !SCREENING_NODE_TYPES.has(node.type)) continue
    const portEdges = edges.filter(
      (e) => e.source === node.id && e.sourceHandle === SCREENING_VOICEMAIL_PORT.id,
    )
    if (portEdges.length < 2) continue
    const real = portEdges.filter((e) => e.deletable !== false)
    const keep = real[0] ?? portEdges[0]
    for (const e of portEdges) {
      if (e !== keep) dropEdges.add(e.id)
    }
  }
  if (dropEdges.size === 0) return { nodes, edges }

  const keptEdges = edges.filter((e) => !dropEdges.has(e.id))
  const stillTargeted = new Set(keptEdges.map((e) => e.target))
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const dropNodes = new Set<string>()
  for (const e of edges) {
    if (!dropEdges.has(e.id) || stillTargeted.has(e.target)) continue
    // the placeholder's auto-minted target is also non-deletable; a node the
    // user placed themselves is deletable and stays even when orphaned
    if (byId.get(e.target)?.deletable === false) dropNodes.add(e.target)
  }
  return {
    nodes: dropNodes.size > 0 ? nodes.filter((n) => !dropNodes.has(n.id)) : nodes,
    edges: keptEdges,
  }
}

/**
 * Single owner of the AI node's voicemail branch. Makes the voicemail intent,
 * its connected node and their edge match the desired target type, and returns
 * the resulting intents array.
 *
 * - desiredType null: remove the intent (the connected node/edge are left on
 *   the canvas — existing behavior)
 * - no intent yet: create intent + node + edge
 * - connected node type differs: rebuild it with fresh defaults. Same type is
 *   left untouched — rf.updateNode replaces node.data wholesale and would wipe
 *   the node's saved config (SER-5762)
 */
export const syncVoicemailTarget = (
  rf: RFInstance,
  aiNode: Node<AiAgentV2NodeData>,
  intents: VoicemailIntents,
  desiredType: FlowNodeTypes | null,
): VoicemailIntents => {
  const existingIntent = getVoicemailIntent(intents)

  if (!desiredType) {
    if (!existingIntent || !intents) return intents
    return intents.filter((intent) => intent.intent_name !== VOICEMAIL_DETECTED_INTENT.name)
  }

  let intentId = existingIntent?.id
  let nextIntents = intents

  if (!existingIntent) {
    intentId = v4()
    nextIntents = [
      ...(intents || []),
      {
        id: intentId,
        intent_name: VOICEMAIL_DETECTED_INTENT.name,
        intent_instructions: VOICEMAIL_DETECTED_INTENT.instructions,
        intent_speech: { speech_enabled: false, intent_speech_text: '' },
      },
    ]
  }

  if (!intentId) return nextIntents

  const connectedNode = getConnectedVoicemailNode(rf, aiNode.id, intentId)

  if (!connectedNode) {
    const newNodeId = v4()
    rf.addNodes({
      id: newNodeId,
      type: desiredType,
      position: { x: aiNode.position.x - 500, y: aiNode.position.y + 300 },
      data: getDefaultNodeData(desiredType, () => rf.getNodes()),
      deletable: false,
    })
    rf.addEdges({
      id: v4(),
      source: aiNode.id,
      target: newNodeId,
      sourceHandle: intentId,
      deletable: false,
    })
  } else if (connectedNode.type !== desiredType) {
    rf.updateNode(connectedNode.id, {
      type: desiredType,
      data: getDefaultNodeData(desiredType, () => rf.getNodes()),
    })
  }

  return nextIntents
}
